/**
 * Organism Templates for Cells & Life
 * Defines all living and non-living specimens used in the game
 */

import type {
  OrganismTemplate,
  NonLivingTemplate,
  ProceduralCellConfig,
} from '@/types';
import { OrganelleType } from '@/types';

/**
 * Helper to create HSL color
 */
const hsl = (h: number, s: number, l: number) => ({ h, s, l });

/**
 * Default membrane configuration
 */
const defaultMembrane = (irregularity: number, baseRadius: number = 50) => ({
  baseRadius,
  irregularity,
  thickness: 3,
  color: hsl(200, 70, 75), // Light blue
  pulseRate: 0.5,
  permeability: 0.5,
});

/**
 * Default cytoplasm configuration
 */
const defaultCytoplasm = (density: number = 0.6) => ({
  density,
  color: hsl(210, 20, 90), // Light gray-blue
});

/**
 * Prokaryotic cell config (no nucleus)
 */
const prokaryoticCell = (
  size: { min: number; max: number },
  irregularity: number,
  organelles: { type: OrganelleType; density?: number }[]
): ProceduralCellConfig => ({
  cellType: 'bacteria',
  size,
  membrane: defaultMembrane(irregularity, (size.min + size.max) / 2),
  nucleus: null,
  organelles,
  cytoplasm: defaultCytoplasm(),
  animation: { drift: 0.5, pulse: 0.3 },
});

/**
 * Eukaryotic animal cell config
 */
const eukaryoticAnimalCell = (
  size: { min: number; max: number },
  irregularity: number,
  organelles: { type: OrganelleType; density?: number }[]
): ProceduralCellConfig => ({
  cellType: 'animal',
  size,
  membrane: defaultMembrane(irregularity, (size.min + size.max) / 2),
  nucleus: {
    relativeSize: 0.2,
    position: 'center',
    nucleolusCount: 1,
    chromatinDensity: 0.5,
  },
  organelles,
  cytoplasm: defaultCytoplasm(),
  animation: { drift: 0.3, pulse: 0.4 },
});

/**
 * Eukaryotic plant cell config
 */
const eukaryoticPlantCell = (
  size: { min: number; max: number },
  organelles: { type: OrganelleType; density?: number }[]
): ProceduralCellConfig => ({
  cellType: 'plant',
  size,
  membrane: {
    ...defaultMembrane(0.05, (size.min + size.max) / 2),
    color: hsl(120, 30, 70), // Greenish
  },
  nucleus: {
    relativeSize: 0.15,
    position: 'offset',
    nucleolusCount: 1,
    chromatinDensity: 0.4,
  },
  organelles,
  cytoplasm: defaultCytoplasm(0.4),
  animation: { pulse: 0.2, chloroplastStream: 0.5 },
});

/**
 * Protist cell config (amoeba-like)
 */
const protistCell = (
  size: { min: number; max: number },
  irregularity: number,
  organelles: { type: OrganelleType; density?: number }[]
): ProceduralCellConfig => ({
  cellType: 'protist',
  size,
  membrane: defaultMembrane(irregularity, (size.min + size.max) / 2),
  nucleus: {
    relativeSize: 0.15,
    position: 'center',
    nucleolusCount: 1,
    chromatinDensity: 0.6,
  },
  organelles,
  cytoplasm: defaultCytoplasm(0.5),
  animation: { drift: 0.4, morph: 0.6, pulse: 0.3 },
});

// ============================================================================
// UNICELLULAR ORGANISMS
// ============================================================================

/**
 * Basic spherical bacterium (coccus)
 */
export const BACTERIUM_BASIC: OrganismTemplate = {
  id: 'bacterium_basic',
  name: 'Bacterium (Coccus)',
  type: 'unicellular',
  domain: 'bacteria',
  cellConfig: {
    ...prokaryoticCell(
      { min: 15, max: 25 },
      0.1,
      [
        { type: OrganelleType.RIBOSOME, density: 0.8 },
        { type: OrganelleType.NUCLEOID },
      ]
    ),
    shape: 'spherical',
  },
  colorVariation: 0.1,
  sizeVariation: 0.2,
  shapeVariation: 0.1,
  movement: { type: 'bacterial-tumble', speed: 2 },
  lifeFunctions: ['binary-fission', 'metabolism', 'response'],
};

/**
 * Rod-shaped bacterium (bacillus)
 */
export const BACTERIUM_ROD: OrganismTemplate = {
  id: 'bacterium_rod',
  name: 'Bacterium (Bacillus)',
  type: 'unicellular',
  domain: 'bacteria',
  cellConfig: {
    ...prokaryoticCell(
      { min: 20, max: 35 },
      0.15,
      [
        { type: OrganelleType.RIBOSOME, density: 0.7 },
        { type: OrganelleType.NUCLEOID },
      ]
    ),
    shape: 'rod',
    // Adjust for elongated shape
    membrane: {
      ...defaultMembrane(0.15, 25),
      baseRadius: 25,
    },
  },
  colorVariation: 0.15,
  sizeVariation: 0.25,
  shapeVariation: 0.2,
  movement: { type: 'bacterial-tumble', speed: 1.5 },
  lifeFunctions: ['binary-fission', 'metabolism', 'response'],
};

/**
 * Spiral bacterium (spirillum)
 */
export const BACTERIUM_SPIRAL: OrganismTemplate = {
  id: 'bacterium_spiral',
  name: 'Bacterium (Spirillum)',
  type: 'unicellular',
  domain: 'bacteria',
  cellConfig: {
    ...prokaryoticCell(
      { min: 25, max: 40 },
      0.3,
      [
        { type: OrganelleType.RIBOSOME, density: 0.6 },
        { type: OrganelleType.NUCLEOID },
      ]
    ),
    shape: 'spiral',
  },
  colorVariation: 0.2,
  sizeVariation: 0.2,
  shapeVariation: 0.3,
  movement: { type: 'flagellar', speed: 3 },
  lifeFunctions: ['binary-fission', 'metabolism', 'response'],
};

/**
 * Amoeba - irregular shape with pseudopods
 */
export const AMOEBA: OrganismTemplate = {
  id: 'amoeba',
  name: 'Amoeba',
  type: 'unicellular',
  domain: 'eukarya',
  kingdom: 'protista',
  cellConfig: protistCell(
    { min: 60, max: 100 },
    0.7,
    [
      { type: OrganelleType.MITOCHONDRIA, density: 0.6 },
      { type: OrganelleType.VACUOLE, density: 0.4 },
      { type: OrganelleType.FOOD_VACUOLE, density: 0.3 },
      { type: OrganelleType.RIBOSOME, density: 0.5 },
      { type: OrganelleType.CONTRACTILE_VACUOLE, density: 0.2 },
    ]
  ),
  colorVariation: 0.2,
  sizeVariation: 0.3,
  shapeVariation: 0.5,
  movement: { type: 'amoeboid', speed: 0.5 },
  lifeFunctions: ['phagocytosis', 'binary-fission', 'response', 'metabolism'],
};

/**
 * Paramecium - slipper-shaped with cilia
 */
export const PARAMECIUM: OrganismTemplate = {
  id: 'paramecium',
  name: 'Paramecium',
  type: 'unicellular',
  domain: 'eukarya',
  kingdom: 'protista',
  cellConfig: {
    ...protistCell(
      { min: 80, max: 120 },
      0.05,
      [
        { type: OrganelleType.CILIA },
        { type: OrganelleType.ORAL_GROOVE },
        { type: OrganelleType.CONTRACTILE_VACUOLE, density: 0.5 },
        { type: OrganelleType.FOOD_VACUOLE, density: 0.4 },
        { type: OrganelleType.MITOCHONDRIA, density: 0.5 },
        { type: OrganelleType.RIBOSOME, density: 0.4 },
      ]
    ),
    animation: { drift: 0.2, pulse: 0.2, cilia: 0.8 },
  },
  colorVariation: 0.1,
  sizeVariation: 0.15,
  shapeVariation: 0.1,
  movement: { type: 'ciliary-glide', speed: 3 },
  lifeFunctions: ['filter-feeding', 'binary-fission', 'response', 'conjugation'],
};

/**
 * Euglena - photosynthetic protist with flagellum
 */
export const EUGLENA: OrganismTemplate = {
  id: 'euglena',
  name: 'Euglena',
  type: 'unicellular',
  domain: 'eukarya',
  kingdom: 'protista',
  cellConfig: protistCell(
    { min: 40, max: 70 },
    0.2,
    [
      { type: OrganelleType.CHLOROPLAST, density: 0.6 },
      { type: OrganelleType.MITOCHONDRIA, density: 0.4 },
      { type: OrganelleType.VACUOLE, density: 0.3 },
      { type: OrganelleType.RIBOSOME, density: 0.4 },
    ]
  ),
  colorVariation: 0.15,
  sizeVariation: 0.2,
  shapeVariation: 0.25,
  movement: { type: 'flagellar', speed: 2.5 },
  lifeFunctions: ['photosynthesis', 'metabolism', 'response', 'binary-fission'],
};

// ============================================================================
// MULTICELLULAR ORGANISMS
// ============================================================================

/**
 * Plant leaf section - packed hexagonal cells with chloroplasts
 */
export const PLANT_LEAF_SECTION: OrganismTemplate = {
  id: 'plant_leaf_section',
  name: 'Plant Leaf Cells',
  type: 'multicellular',
  domain: 'eukarya',
  kingdom: 'plantae',
  cellConfig: eukaryoticPlantCell(
    { min: 30, max: 45 },
    [
      { type: OrganelleType.CHLOROPLAST, density: 0.8 },
      { type: OrganelleType.CENTRAL_VACUOLE },
      { type: OrganelleType.MITOCHONDRIA, density: 0.3 },
      { type: OrganelleType.RIBOSOME, density: 0.3 },
    ]
  ),
  cellArrangement: {
    pattern: 'packed-hexagonal',
    cellCount: { min: 20, max: 50 },
    layerTypes: ['mesophyll'],
  },
  colorVariation: 0.1,
  sizeVariation: 0.1,
  shapeVariation: 0.05,
  movement: { type: 'static', speed: 0 },
  lifeFunctions: ['photosynthesis', 'transpiration', 'growth'],
};

/**
 * Animal skin cells - epithelial tissue
 */
export const ANIMAL_SKIN_CELLS: OrganismTemplate = {
  id: 'animal_skin_cells',
  name: 'Animal Skin Cells',
  type: 'multicellular',
  domain: 'eukarya',
  kingdom: 'animalia',
  cellConfig: eukaryoticAnimalCell(
    { min: 25, max: 40 },
    0.1,
    [
      { type: OrganelleType.MITOCHONDRIA, density: 0.5 },
      { type: OrganelleType.RIBOSOME, density: 0.4 },
      { type: OrganelleType.ENDOPLASMIC_RETICULUM, density: 0.3 },
    ]
  ),
  cellArrangement: {
    pattern: 'packed-hexagonal',
    cellCount: { min: 15, max: 35 },
    layerTypes: ['epithelial'],
  },
  colorVariation: 0.15,
  sizeVariation: 0.15,
  shapeVariation: 0.1,
  movement: { type: 'static', speed: 0 },
  lifeFunctions: ['metabolism', 'growth'],
};

// ============================================================================
// TRICKY SPECIMENS (Edge Cases)
// ============================================================================

/**
 * Virus particle - NOT living, but has structure
 */
export const VIRUS_PARTICLE: OrganismTemplate = {
  id: 'virus_particle',
  name: 'Virus Particle',
  type: 'unicellular', // Technically not, but for display purposes
  domain: 'bacteria', // Not accurate, but for config purposes
  cellConfig: prokaryoticCell(
    { min: 8, max: 15 },
    0.0,
    [] // No organelles - just geometric shell
  ),
  colorVariation: 0.05,
  sizeVariation: 0.1,
  shapeVariation: 0.0,
  movement: { type: 'static', speed: 0 },
  lifeFunctions: [], // No life functions - NOT living
};

/**
 * Dormant bacterium - living but appears inactive
 */
export const DORMANT_BACTERIUM: OrganismTemplate = {
  id: 'dormant_bacterium',
  name: 'Dormant Bacterium (Endospore)',
  type: 'unicellular',
  domain: 'bacteria',
  cellConfig: {
    ...prokaryoticCell(
      { min: 10, max: 18 },
      0.0,
      [{ type: OrganelleType.NUCLEOID }]
    ),
    membrane: {
      ...defaultMembrane(0.0, 14),
      thickness: 5, // Thick protective coat
      color: hsl(40, 30, 60), // Brownish
    },
  },
  colorVariation: 0.05,
  sizeVariation: 0.1,
  shapeVariation: 0.0,
  movement: { type: 'static', speed: 0 },
  lifeFunctions: ['metabolism'], // Minimal, but alive
};

// ============================================================================
// NON-LIVING SPECIMENS
// ============================================================================

/**
 * Salt crystal - geometric, grows but not alive
 */
export const CRYSTAL: NonLivingTemplate = {
  id: 'crystal',
  name: 'Salt Crystal',
  confusingFeatures: ['grows', 'has-structure', 'refracts-light'],
  distinguishingFeatures: ['no-cells', 'no-metabolism', 'geometric-shape', 'no-membrane'],
};

/**
 * Dust particle - organic-looking but not alive
 */
export const DUST_PARTICLE: NonLivingTemplate = {
  id: 'dust_particle',
  name: 'Dust Particle',
  confusingFeatures: ['organic-looking', 'irregular-shape', 'brownish-color'],
  distinguishingFeatures: ['no-cells', 'no-organization', 'no-internal-structure'],
};

/**
 * Water bubble - membrane-like but not alive
 */
export const WATER_BUBBLE: NonLivingTemplate = {
  id: 'water_bubble',
  name: 'Water Bubble',
  confusingFeatures: ['membrane-like', 'moves-with-flow', 'spherical'],
  distinguishingFeatures: ['no-internal-structure', 'uniform-content', 'no-organelles'],
};

/**
 * Pollen grain - was alive but now dormant structure
 */
export const POLLEN_GRAIN: NonLivingTemplate = {
  id: 'pollen_grain',
  name: 'Pollen Grain',
  confusingFeatures: ['has-structure', 'organic-origin', 'textured-surface'],
  distinguishingFeatures: ['no-active-metabolism', 'no-response', 'dormant-structure'],
};

/**
 * Oil droplet - spherical like a cell
 */
export const OIL_DROPLET: NonLivingTemplate = {
  id: 'oil_droplet',
  name: 'Oil Droplet',
  confusingFeatures: ['spherical', 'moves', 'has-boundary'],
  distinguishingFeatures: ['no-cells', 'no-internal-structure', 'uniform-composition'],
};

// ============================================================================
// COLLECTIONS
// ============================================================================

/**
 * All organism templates indexed by ID
 */
export const ORGANISM_TEMPLATES: Record<string, OrganismTemplate> = {
  bacterium_basic: BACTERIUM_BASIC,
  bacterium_rod: BACTERIUM_ROD,
  bacterium_spiral: BACTERIUM_SPIRAL,
  amoeba: AMOEBA,
  paramecium: PARAMECIUM,
  euglena: EUGLENA,
  plant_leaf_section: PLANT_LEAF_SECTION,
  animal_skin_cells: ANIMAL_SKIN_CELLS,
  virus_particle: VIRUS_PARTICLE,
  dormant_bacterium: DORMANT_BACTERIUM,
};

/**
 * All non-living templates indexed by ID
 */
export const NON_LIVING_TEMPLATES: Record<string, NonLivingTemplate> = {
  crystal: CRYSTAL,
  dust_particle: DUST_PARTICLE,
  water_bubble: WATER_BUBBLE,
  pollen_grain: POLLEN_GRAIN,
  oil_droplet: OIL_DROPLET,
};

/**
 * Get organism template by ID
 */
export function getOrganismTemplate(id: string): OrganismTemplate | undefined {
  return ORGANISM_TEMPLATES[id];
}

/**
 * Get non-living template by ID
 */
export function getNonLivingTemplate(id: string): NonLivingTemplate | undefined {
  return NON_LIVING_TEMPLATES[id];
}

/**
 * Get all unicellular organisms
 */
export function getUnicellularOrganisms(): OrganismTemplate[] {
  return Object.values(ORGANISM_TEMPLATES).filter(o => o.type === 'unicellular');
}

/**
 * Get all multicellular organisms
 */
export function getMulticellularOrganisms(): OrganismTemplate[] {
  return Object.values(ORGANISM_TEMPLATES).filter(o => o.type === 'multicellular');
}

/**
 * Get organisms by domain
 */
export function getOrganismsByDomain(domain: OrganismTemplate['domain']): OrganismTemplate[] {
  return Object.values(ORGANISM_TEMPLATES).filter(o => o.domain === domain);
}

/**
 * Check if a specimen ID is living
 */
export function isLivingSpecimen(id: string): boolean {
  const organism = ORGANISM_TEMPLATES[id];
  if (!organism) return false;

  // Virus is not living (no life functions)
  if (organism.lifeFunctions.length === 0) return false;

  return true;
}

/**
 * Check if a specimen ID is non-living
 */
export function isNonLivingSpecimen(id: string): boolean {
  return NON_LIVING_TEMPLATES[id] !== undefined;
}
